// Who has ended, for the capture screens of the active business: worker number
// → end date (YYYY-MM-DD), for its own workers and those seconded in, end date
// on or before today (capture_worker_end_dates, mig 20261037100000).
//
// Advisory only. A failed or offline read means no warning, never a refused
// capture — the server still notes the record. A plain object, not a Map, so
// the persisted query cache can carry it offline (main.tsx isPersistSafe), and
// "Prepare for offline" warms it (offlineLookups).
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { withReadTimeout } from '@/lib/saveTimeout'
import { useActiveTenantId } from '@/hooks/useActiveTenantId'

export type EndDates = Record<string, string>

async function fetchCaptureEndDates(signal?: AbortSignal): Promise<EndDates> {
  const call = supabase.rpc('capture_worker_end_dates')
  const { data, error } = await withReadTimeout(
    signal ? call.abortSignal(signal) : call, 'capture_worker_end_dates')
  if (error) throw error
  return endDatesFromRows(data)
}

/** The rows → { worker_number: end_date } shape — shared with the offline
 *  pre-cache (offlineLookups), which must fill the SAME cache entry. */
export function endDatesFromRows(data: unknown): EndDates {
  const out: EndDates = {}
  for (const r of (data ?? []) as { worker_number: string; end_date: string }[]) out[r.worker_number] = r.end_date
  return out
}

export function useCaptureEndDates() {
  const tenantId = useActiveTenantId()
  return useQuery<EndDates>({
    queryKey: ['capture-end-dates', tenantId],
    enabled:  !!tenantId,
    staleTime: 10 * 60_000,
    retry: false,
    queryFn: ({ signal }) => fetchCaptureEndDates(signal),
  })
}
